import { useEffect, useMemo, useRef } from "react";
import {
  type HousingProduct,
  HousingProductType,
  HousingType,
  isKITProduct,
  type ProductData,
} from "~/domains/product/types/ProductData";
import { ImageFormat } from "~/domains/product/types/ProductDataDTO";
import { ProductTabularViewActionCell } from "~/features/search/components/ProductResults/views/TabularView/ProductTabularViewActionCell/ProductTabularViewActionCell";
import { Translation } from "~/app/i18n/Translation";
import { PopperTooltip } from "~/common/components/Tooltip";
import CenterContents from "~/common/components/CenterContents";
import { Link, LinkType } from "~/common/components/Link";
import { DesignationHeaderCell } from "~/features/search/components/ProductResults/views/TabularView/ProductTabularViewHeaderCell";
import { isSiteRegion } from "~/utils/siteModes";
import { CountryCode } from "~/utils/countryData";
import throttle from "lodash/throttle";
import { type ColumnDef } from "@tanstack/react-table";
import { TanStackTable } from "~/common/components/Table/TanStackTable";
import classNames from "classnames";

import classes from "~/features/selectionAssistant/housing/components/VariantProductsTableModal/HousingTabularViewDetailPanel.module.less";
import {
  ACTIONS_COL_WIDTH,
  calculateTotalTableWidth,
  renderNestedHSADesignationCell,
} from "~/features/search/components/ProductResults/views/TabularView/TanStackColumnDefs";
import { useIsMinDesktopSize, useIsMinDesktopSizeM, useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import { CONTAINER_DESKTOP_M, CONTAINER_DESKTOP_S } from "~/common/utils/screenSizes";
import { getPrimaryImage } from "~/utils/product/getPrimaryImage";

import { isNonNull } from "~/common/utils/reducers";
import { TranslationType } from "~/common/types/TranslationModel";

interface Props {
  products: ProductData[];
}

export const HousingTabularViewDetailPanel = ({ products: variantProducts }: Props) => {
  const rows = useMemo(() => variantProducts.map(transformVariantProduct).filter(isNonNull), [variantProducts]);
  const isMinTable = useIsMinTabletSize();
  const isDesktop = useIsMinDesktopSize();
  const isLargeDesktop = useIsMinDesktopSizeM();

  const tanstackColumns = useDetailTableConfig();

  const totalTableWidth = calculateTotalTableWidth(tanstackColumns);
  const isTableLargerThanDesktop = totalTableWidth > (isLargeDesktop ? CONTAINER_DESKTOP_M : CONTAINER_DESKTOP_S);

  const ref = useRef<HTMLDivElement>(null);

  let state = undefined;
  let fullwidth = true;
  if (!isMinTable) {
    state = { columnPinning: { left: ["name"] } };
    fullwidth = false;
  } else if (!isDesktop) {
    state = { columnPinning: { left: ["name"], right: ["actions"] } };
    fullwidth = false;
  } else if (isTableLargerThanDesktop) {
    state = { columnPinning: { left: ["name"], right: ["actions"] } };
    fullwidth = false;
  }

  return (
    <div className={classNames(classes.detailPanel)} ref={ref}>
      <TanStackTable
        data={rows}
        className={classes.detailPanelInner}
        columns={tanstackColumns}
        state={state}
        variant={"hsaDetails"}
        fullwidth={fullwidth}
      />
    </div>
  );
};

interface DetailTableRow {
  includedParts: DetailTablePart[];
  optionalParts: DetailTablePart[];
  variant: HousingType.UNIVERSAL | HousingType.KIT;
  product: ProductData;
}

interface DetailTablePart {
  designation: string;
  imgUrl: string;
  url: string;
  quantity: number;
}

const INCLUDED_PART_ORDER = [
  HousingProductType.SEAL,
  HousingProductType.SEAL,
  HousingProductType.END_COVER,
  HousingProductType.LOCATING_RING,
];

const OPTIONAL_PART_ORDER = [
  HousingProductType.BEARING,
  HousingProductType.ADAPTER_SLEEVE,
  HousingProductType.LOCKNUT,
  HousingProductType.WASHER,
  HousingProductType.SEAL,
  HousingProductType.SEAL,
  HousingProductType.END_COVER,
];

const TRANSLATION_KEYS: Partial<Record<HousingProductType, string>> = {
  [HousingProductType.SEAL]: "page.housingSelectionAssistant.productType.SEAL",
  [HousingProductType.END_COVER]: "page.housingSelectionAssistant.productType.END_COVER",
  [HousingProductType.LOCATING_RING]: "page.housingSelectionAssistant.productType.LOCATING_RING",
  [HousingProductType.BEARING]: "page.housingSelectionAssistant.productType.BEARING",
  [HousingProductType.ADAPTER_SLEEVE]: "page.housingSelectionAssistant.productType.ADAPTER_SLEEVE",
  [HousingProductType.LOCKNUT]: "page.housingSelectionAssistant.productType.LOCKNUT",
  [HousingProductType.WASHER]: "page.housingSelectionAssistant.productType.WASHER",
};
const WIDTHS: Partial<Record<HousingProductType, number>> = {
  [HousingProductType.SEAL]: 12 * 2 + 136,
  [HousingProductType.END_COVER]: 12 * 2 + 118,
  [HousingProductType.LOCATING_RING]: 12 * 2 + 118,
  [HousingProductType.BEARING]: 12 * 2 + 118,
  [HousingProductType.ADAPTER_SLEEVE]: 12 * 2 + 118,
  [HousingProductType.LOCKNUT]: 12 * 2 + 118,
  [HousingProductType.WASHER]: 12 * 2 + 118,
};

function transformVariantProduct(product: ProductData): DetailTableRow | null {
  if (!isKITProduct(product) || product.housingType === HousingType.PRODUCT_LINE_72) {
    return null;
  }

  return {
    product,
    variant: product.housingType,
    includedParts: transformParts(product.includedParts, INCLUDED_PART_ORDER),
    optionalParts: transformParts(product.optionalParts, OPTIONAL_PART_ORDER),
  };
}

export const useDetailTableConfig = () => {
  const isDesktop = useIsMinDesktopSize();
  const isMinTable = useIsMinTabletSize();
  return useMemo(() => {
    const includedPartsColumns = INCLUDED_PART_ORDER.map((type, index) => {
      return makePartColumnConfig(
        (row) => row.includedParts[index],
        TRANSLATION_KEYS[type] ?? "",
        WIDTHS[type] ?? 0,
        index === 0 ? "page.housingSelectionAssistant.details.included.title" : undefined,
        "white",
        index === 0,
        INCLUDED_PART_ORDER.length,
      );
    });

    const optionalPartsColumns = OPTIONAL_PART_ORDER.map((type, index) => {
      return makePartColumnConfig(
        (row) => row.optionalParts[index],
        TRANSLATION_KEYS[type] ?? "",
        WIDTHS[type] ?? 0,
        index === 0 ? "page.housingSelectionAssistant.details.optional.title" : undefined,
        "transparent",
        false,
      );
    });

    let columnConfig: ColumnDef<DetailTableRow>[];
    if (isSiteRegion(CountryCode.UNITED_STATES)) {
      columnConfig = includedPartsColumns.concat(optionalPartsColumns);
    } else {
      columnConfig = optionalPartsColumns.concat(includedPartsColumns);
    }

    let designationWidth;
    if (!isMinTable) {
      designationWidth = 120;
    } else if (!isDesktop) {
      designationWidth = 278;
    } else {
      designationWidth = 350;
    }

    columnConfig.push({
      id: "name",
      header: () => <DesignationHeaderCell className={"scha__hsa__detail-panel__table-header-cell"} />,
      size: designationWidth,
      cell: (cell) => renderNestedHSADesignationCell(true)(cell.row.original),
      // cellStyle: makeHsaCellStyle(),
      // headerStyle: makeHsaCellStyle(),
    });

    columnConfig.push({
      id: "actions",
      header: "",
      cell: (cell) => {
        return <ProductTabularViewActionCell productCode={cell.row.original.product.code} isHousing={true} />;
      },
      size: ACTIONS_COL_WIDTH,
      meta: {
        columnType: "action",
        className: "scha__tabular-view__action-cell-bg",
      },
      // headerStyle: {
      //   padding: 0,
      // },
    });

    return columnConfig;
  }, []);
};

function transformParts(parts: HousingProduct[], partOrder: HousingProductType[]): DetailTablePart[] {
  const result: DetailTablePart[] = [];
  parts = [...parts];
  for (const type of partOrder) {
    const index = parts.findIndex((item) => item.type === type);
    if (index !== -1) {
      const item = parts[index];
      parts.splice(index, 1);
      result.push({
        designation: item.designation,
        url: item.product.url,
        imgUrl: getPrimaryImage(item.product.images, { imageFormat: ImageFormat.product })?.url ?? "",
        quantity: item.quantity,
      });
    } else {
      result.push({
        designation: "-",
        url: "",
        imgUrl: "",
        quantity: 0,
      });
    }
  }
  return result;
}

export const HSA_TABLE_CONTAINER_JS_CLASS = "js__hsa-result-table";

interface PartColumnHeaderProps {
  labelKey: string;
  sectionTitleKey?: string;
  addBorder?: boolean;
  borderCellAmount?: number;
}

const PartColumnHeader = ({ labelKey, sectionTitleKey, addBorder, borderCellAmount = 1 }: PartColumnHeaderProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const borderRef = useRef<HTMLDivElement>(null);
  const headerCls = "scha__hsa__detail-panel__table-header-cell";
  const sectionTitleCls = "scha__hsa__detail-panel__table-section-title";

  useEffect(() => {
    const fn = throttle(() => {
      if (ref.current && addBorder) {
        const table = ref.current.closest("table");
        const borderHeight = table?.getBoundingClientRect().height ?? 0;

        const headerCells = Array.prototype.slice.call(
          ref.current.closest("thead")!.querySelectorAll("th"),
        ) as HTMLElement[];
        let borderWidth = 0;
        let start = false;
        let counter = 0;
        for (const cell of headerCells) {
          const titleTag = cell.querySelector(`.${sectionTitleCls}`);
          if (!start && titleTag === ref.current) {
            start = true;
          } else if (counter === borderCellAmount) {
            break;
          }

          if (start) {
            borderWidth += cell.getBoundingClientRect().width;
            counter += 1;
          }
        }

        if (borderRef.current) {
          borderRef.current.style.width = `${borderWidth}px`;
          borderRef.current.style.height = `${borderHeight}px`;
        }
      }
    }, 16);

    fn();
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, [addBorder, borderCellAmount]);

  return (
    <div className={headerCls}>
      <Translation type={TranslationType.INTERNAL} value={labelKey} />
      {sectionTitleKey && (
        <p className={sectionTitleCls} ref={ref}>
          <Translation type={TranslationType.INTERNAL} value={sectionTitleKey} />
        </p>
      )}
      {addBorder && <div className={"scha__hsa__detail-panel__table-section-border"} ref={borderRef} />}
    </div>
  );
};

function makePartColumnConfig(
  partGetter: (row: DetailTableRow) => DetailTablePart,
  labelKey: string,
  width: number,
  sectionTitleKey: string | undefined,
  backgroundColor: string,
  addBorder: boolean,
  borderCellAmount?: number,
): ColumnDef<DetailTableRow> {
  return {
    id: labelKey,
    header: () => (
      <PartColumnHeader
        labelKey={labelKey}
        sectionTitleKey={sectionTitleKey}
        addBorder={addBorder}
        borderCellAmount={borderCellAmount}
      />
    ),
    size: width,
    // cellStyle: makeHsaCellStyle(backgroundColor),
    // headerStyle: makeHsaCellStyle(backgroundColor),
    cell: (cell) => <DetailCell {...partGetter(cell.row.original)} />,
  };
}

const DetailCell = ({ designation, url, quantity, imgUrl }: DetailTablePart) => {
  const qtyText = designation === "-" ? null : <span className="f-label-color"> ({quantity})</span>;

  let designationLink = url ? (
    <Link variant={LinkType.NAVIGATION} href={url} className="f-bold d-inline">
      {designation}
    </Link>
  ) : (
    <span className="f-bold">{designation}</span>
  );

  if (imgUrl) {
    designationLink = (
      <PopperTooltip
        zIndex={1300}
        className={"d-inline"}
        tooltipContent={
          <CenterContents className="scha__hsa__detail-panel__image-popup">
            <img src={imgUrl} />
          </CenterContents>
        }
      >
        {designationLink}
      </PopperTooltip>
    );
  }

  return (
    <div className={"label-large"}>
      {designationLink}
      {qtyText}
    </div>
  );
};
